import { App } from '@capacitor/app';

import { isNativeApp } from './positionSource';

/**
 * Botón «atrás» de Android — pila de manejadores.
 *
 * ## Por qué hace falta una pila y no un listener suelto
 *
 * Verificado leyendo el código Java que corre en el APK
 * (`node_modules/@capacitor/app/android/.../AppPlugin.java`, v8.1.1):
 *
 * ```java
 * if (!hasListeners(EVENT_BACK_BUTTON)) {
 *     if (bridge.getWebView().canGoBack()) bridge.getWebView().goBack();
 * } else {
 *     notifyListeners(EVENT_BACK_BUTTON, data, true);
 * }
 * ```
 *
 * Es un `if/else`: en cuanto EXISTE un listener de JS, Capacitor deja de hacer
 * su `goBack()` — el listener se queda con TODO el botón atrás de la app. Por
 * eso registrar uno global «por si acaso» es un cambio de comportamiento de
 * toda la aplicación, no un añadido local.
 *
 * (Las issues que circulan diciendo que el listener «no desactiva la
 * navegación por defecto» —capacitor#2220, capacitor-plugins#869— están
 * CERRADAS y son de Capacitor 3; además muchas venían de apps con Ionic
 * Framework, que registra su propio listener y navega por su cuenta, de ahí la
 * impresión de que pasaban las dos cosas.)
 *
 * ## La decisión de diseño
 *
 * El listener nativo **solo existe mientras hay algún manejador registrado**.
 * Con la pila vacía no hay listener, así que Capacitor hace exactamente lo que
 * hacía antes de que este fichero existiera: riesgo cero en el 99 % de la app.
 *
 * Orden LIFO: gana el último que se registró, que es el que está más «encima»
 * en pantalla.
 *
 * Si ningún manejador se lo queda, se replica el comportamiento nativo: volver
 * atrás si hay historial, y **no hacer nada si no lo hay**. Ojo con esto
 * último — hoy la app NO se cierra con el botón atrás, y esta pieza no es el
 * sitio para cambiar eso.
 *
 * Fuera de la app nativa no hace absolutamente nada (hay tripwire).
 */

/**
 * Devuelve `false` para dejar pasar la pulsación al siguiente manejador de la
 * pila. Cualquier otra cosa (incluido no devolver nada) la da por consumida.
 */
export type BackButtonHandler = () => boolean | void;

const handlers: BackButtonHandler[] = [];

let nativeListener: { remove: () => Promise<void> } | null = null;
/** Hay un `addListener` en vuelo cuya promesa aún no ha resuelto. */
let attaching = false;

/** Lo que hace Capacitor cuando no hay ningún listener de JS. */
const runNativeDefault = (canGoBack: boolean): void => {
  if (canGoBack) window.history.back();
};

const dispatch = ({ canGoBack }: { canGoBack: boolean }): void => {
  // Copia y recorrido del más reciente al más antiguo: un manejador puede
  // desregistrarse a sí mismo desde dentro (cerrar lo que estaba abierto es
  // justo lo normal), y eso mutaría el array mientras se recorre.
  const snapshot = [...handlers];
  for (let i = snapshot.length - 1; i >= 0; i -= 1) {
    if (snapshot[i]() !== false) return;
  }
  runNativeDefault(canGoBack);
};

const attachIfNeeded = (): void => {
  if (nativeListener || attaching) return;
  attaching = true;
  void App.addListener('backButton', dispatch).then((handle) => {
    attaching = false;
    // Mientras la promesa estaba en vuelo la pila pudo vaciarse.
    if (handlers.length === 0) {
      void handle.remove();
      return;
    }
    nativeListener = handle;
  });
};

const detachIfIdle = (): void => {
  if (handlers.length > 0 || !nativeListener) return;
  const handle = nativeListener;
  nativeListener = null;
  void handle.remove();
};

/**
 * Registra un manejador del botón atrás. Devuelve la función para retirarlo.
 * Fuera de la app nativa devuelve un no-op sin tocar nada.
 */
export function pushBackButtonHandler(handler: BackButtonHandler): () => void {
  if (!isNativeApp()) return () => undefined;

  handlers.push(handler);
  attachIfNeeded();

  let released = false;
  return () => {
    if (released) return;
    released = true;
    const index = handlers.lastIndexOf(handler);
    if (index !== -1) handlers.splice(index, 1);
    detachIfIdle();
  };
}

/** Solo para tests: deja el módulo como recién importado. */
export function resetBackButtonHandlersForTests(): void {
  handlers.length = 0;
  nativeListener = null;
  attaching = false;
}
